import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ArrowDownRight, Lock, X } from "lucide-react";

export const Card = ({ children, className = "", testid, as: Tag = "div", ...rest }) => (
  <Tag
    data-testid={testid}
    className={`min-w-0 rounded-2xl border border-[#E3E8E9] bg-white shadow-[0_1px_2px_rgba(40,78,85,0.04)] ${className}`}
    {...rest}
  >
    {children}
  </Tag>
);

export const PageHeader = ({ eyebrow, title, sub, actions, testid }) => (
  <div data-testid={testid} className="mb-7 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
    <div>
      {eyebrow && <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-petrol">{eyebrow}</p>}
      <h1 className="mt-1.5 font-display text-3xl font-medium tracking-tight text-navy sm:text-4xl">{title}</h1>
      {sub && <p className="mt-2 max-w-2xl text-sm leading-relaxed text-stone-500">{sub}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const SectionTitle = ({ children, right }) => (
  <div className="mb-4 flex items-center justify-between gap-3">
    <h2 className="font-display text-xl font-medium text-navy sm:text-2xl">{children}</h2>
    {right}
  </div>
);

const TONES = {
  petrol: "bg-petrol-tint text-petrol",
  copper: "bg-cognac/10 text-cognac-dark",
  sage: "bg-sage/10 text-sage",
  stone: "bg-stone-100 text-stone-600",
  amber: "bg-amber-50 text-amber-700",
  red: "bg-red-50 text-red-700",
  white: "bg-white text-navy border border-[#E3E8E9]",
};

export const Pill = ({ tone = "stone", children, className = "", testid }) => (
  <span
    data-testid={testid}
    className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-bold ${TONES[tone]} ${className}`}
  >
    {children}
  </span>
);

export const TypeBadge = ({ type }) =>
  type === "moe" ? <Pill tone="petrol">Maîtrise d'œuvre</Pill> : <Pill tone="copper">Petits travaux</Pill>;

const STATUS_TONES = {
  Nouveau: "petrol",
  "À contacter": "amber",
  "RDV prévu": "sage",
  "Proposition envoyée": "copper",
  Gagné: "sage",
  Perdu: "stone",
  "À fournir": "amber",
  "En vérification": "petrol",
  Validé: "sage",
  Refusé: "red",
  Expiré: "red",
  Actif: "sage",
  Exonéré: "sage",
  Impayé: "red",
  Résilié: "stone",
  Suspendu: "amber",
  Conforme: "sage",
  "Non conforme": "red",
  "À compléter": "amber",
  "Document expiré": "red",
  "Demande client": "petrol",
  "Visite / métrés": "petrol",
  Besoin: "petrol",
  Artisan: "amber",
  Devis: "amber",
  Accepté: "copper",
  Travaux: "copper",
  Facture: "amber",
  Terminé: "sage",
};

export const StatusBadge = ({ status, testid }) => (
  <Pill tone={STATUS_TONES[status] || "stone"} testid={testid}>
    {status}
  </Pill>
);

export const Delta = ({ current, prev, suffix = "" }) => {
  if (!prev) return null;
  const pct = Math.round(((current - prev) / prev) * 100);
  const up = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-bold ${up ? "text-sage" : "text-red-600"}`}>
      {up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
      {up ? "+" : ""}
      {pct} %{suffix}
    </span>
  );
};

export const Btn = ({ children, variant = "primary", className = "", testid, to, ...rest }) => {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-50";
  const styles = {
    primary: "bg-petrol text-white hover:bg-petrol-deep",
    copper: "bg-cognac text-white hover:bg-cognac-dark",
    ghost: "border border-[#D9E0E2] bg-white text-navy hover:border-petrol hover:text-petrol",
    soft: "bg-petrol-tint text-petrol hover:bg-petrol/15",
  };
  const cls = `${base} ${styles[variant]} ${className}`;
  if (to) {
    return (
      <Link to={to} data-testid={testid} className={cls} {...rest}>
        {children}
      </Link>
    );
  }
  return (
    <button data-testid={testid} className={cls} {...rest}>
      {children}
    </button>
  );
};

export const Segmented = ({ options, value, onChange, testid, size = "md" }) => (
  <div data-testid={testid} className="inline-flex flex-wrap rounded-full border border-[#D9E0E2] bg-white p-1">
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        data-testid={`${testid}-${o.value}`}
        onClick={() => onChange(o.value)}
        className={`rounded-full font-bold transition-colors duration-200 ${size === "sm" ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm"} ${
          value === o.value ? "bg-petrol text-white" : "text-stone-500 hover:text-petrol"
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

export const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-bold text-stone-500">{label}</span>
    {children}
  </label>
);

export const inputCls =
  "w-full rounded-xl border border-[#D9E0E2] bg-white px-3.5 py-2.5 text-sm text-navy outline-none transition-colors placeholder:text-stone-400 focus:border-petrol";

export const SlideOver = ({ open, onClose, title, eyebrow, children, testid, footer }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[9998] bg-navy/30 backdrop-blur-[2px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            data-testid={`${testid}-backdrop`}
          />
          <motion.aside
            data-testid={testid}
            role="dialog"
            aria-modal="true"
            className="fixed inset-y-0 right-0 z-[9999] flex w-full max-w-lg flex-col bg-white shadow-2xl"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-[#EDF0F1] px-6 py-5">
              <div>
                {eyebrow && <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-petrol">{eyebrow}</p>}
                <h2 className="mt-1 font-display text-2xl font-medium text-navy">{title}</h2>
              </div>
              <button
                data-testid={`${testid}-close`}
                onClick={onClose}
                aria-label="Fermer"
                className="rounded-full border border-[#D9E0E2] p-2 text-stone-500 transition-colors hover:border-petrol hover:text-petrol"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div
              data-lenis-prevent
              className="min-h-0 flex-1 overflow-y-auto px-6 py-5"
              style={{ WebkitOverflowScrolling: "touch", overscrollBehavior: "contain" }}
            >
              {children}
            </div>
            {footer && <div className="shrink-0 border-t border-[#EDF0F1] px-6 py-4">{footer}</div>}
          </motion.aside>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export const InfoRow = ({ label, value, testid }) => (
  <div data-testid={testid} className="flex items-start justify-between gap-4 border-b border-[#F0F2F3] py-2.5 text-sm last:border-b-0">
    <span className="shrink-0 text-stone-500">{label}</span>
    <span className="text-right font-semibold text-navy">{value}</span>
  </div>
);

export const Locked = ({ title, testid = "locked-feature" }) => (
  <Card testid={testid} className="mx-auto mt-6 max-w-xl p-8 text-center sm:p-12">
    <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-petrol-tint text-petrol">
      <Lock className="h-6 w-6" />
    </span>
    <p className="mt-5 text-[11px] font-bold uppercase tracking-[0.22em] text-petrol">{title}</p>
    <h2 className="mt-2 font-display text-2xl font-medium text-navy sm:text-3xl">
      Disponible après validation de votre dossier CozyHome.
    </h2>
    <p className="mt-3 text-sm leading-relaxed text-stone-500">
      Cette fonction s'ouvrira automatiquement dès que CozyHome aura vérifié et validé votre dossier
      professionnel. En attendant, vous pouvez compléter votre dossier depuis « Mon entreprise ».
    </p>
    <Btn to="/partenaire/entreprise" testid={`${testid}-cta`} className="mt-6">
      Compléter mon dossier
    </Btn>
  </Card>
);

export const Placeholder = ({ children, testid }) => (
  <div
    data-testid={testid}
    className="rounded-xl border border-dashed border-[#D9E0E2] bg-petrol-bg px-4 py-6 text-center text-sm text-stone-500"
  >
    {children}
  </div>
);
